import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Col, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const TAG_MORE_CHAR_THRESHOLD = 45;

type AccountProfileCardProps = {
  title: string;
  children: ReactNode;
};

export function AccountProfileCard({ title, children }: AccountProfileCardProps) {
  return (
    <section className="my-account-card">
      <h3 className="my-account-card__title">{title}</h3>
      {children}
    </section>
  );
}

type AccountSectionProps = {
  title: string;
  children: ReactNode;
  /** Full-width body (e.g. About text) instead of a field grid */
  body?: boolean;
};

/** @deprecated Prefer AccountProfileCard for the redesigned my-account page */
export function AccountSection({ title, children, body = false }: AccountSectionProps) {
  return (
    <section className="card border shadow-sm rounded-3 overflow-hidden mt-3">
      <div className="card-header bg-light border-bottom py-3">
        <h3 className="h6 mb-0 fw-bold text-truncate">{title}</h3>
      </div>
      {body ? <div className="card-body">{children}</div> : <Row className="g-0">{children}</Row>}
    </section>
  );
}

const CLIP_CLAMP: CSSProperties = {
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

type ExpandableTextProps = {
  children: ReactNode;
  className?: string;
  /** Multi-line clamp instead of single-line truncate */
  clamp?: boolean;
};

/** Truncates overflowing text. Used by UserProfile / ProfileModalFields. */
export function ExpandableText({
  children,
  className = "",
  clamp = false,
}: ExpandableTextProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return;

    const measure = () => {
      const isOverflow = clamp
        ? el.scrollHeight > el.clientHeight + 1
        : el.scrollWidth > el.clientWidth + 1;
      setOverflows(isOverflow);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children, expanded, clamp]);

  const showDots = !expanded && overflows;

  if (clamp) {
    return (
      <div className={`min-w-0 ${className}`.trim()}>
        <div ref={textRef} style={expanded ? undefined : CLIP_CLAMP}>
          {children}
        </div>
        {showDots ? (
          <button
            type="button"
            className="btn p-0 border-0 bg-transparent fw-bold text-primary lh-1"
            style={{ fontSize: "1.35rem", letterSpacing: "0.05em" }}
            aria-label="Show more"
            onClick={() => setExpanded(true)}
          >
            …
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`d-flex align-items-baseline min-w-0 ${className}`.trim()}
      style={{ maxWidth: "100%" }}
    >
      <div
        ref={textRef}
        className={`min-w-0 flex-grow-1 ${expanded ? "" : "text-truncate"}`.trim()}
        style={expanded ? { overflowWrap: "anywhere" } : undefined}
      >
        {children}
      </div>
      {showDots ? (
        <button
          type="button"
          className="btn p-0 border-0 fw-bold text-primary flex-shrink-0 lh-1"
          style={{
            fontSize: "1.35rem",
            letterSpacing: "0.05em",
            marginLeft: "-0.85em",
            paddingLeft: "0.15em",
            backgroundColor: "var(--bs-card-bg, #fff)",
          }}
          aria-label="Show more"
          onClick={() => setExpanded(true)}
        >
          …
        </button>
      ) : null}
    </div>
  );
}

type AccountFieldProps = {
  label: string;
  value: ReactNode;
  fullWidth?: boolean;
};

export function AccountField({
  label,
  value,
  fullWidth = false,
}: AccountFieldProps) {
  return (
    <Col
      xs={12}
      md={fullWidth ? 12 : 6}
      lg={fullWidth ? 12 : 4}
      className="border-bottom p-3 min-w-0"
    >
      <div className="small text-muted fw-semibold mb-1 text-truncate">{label}</div>
      <ExpandableText className="fw-semibold" clamp={fullWidth}>
        {value}
      </ExpandableText>
    </Col>
  );
}

type FieldDetailModalProps = {
  show: boolean;
  onHide: () => void;
  label: string;
  children: ReactNode;
};

function FieldDetailModal({ show, onHide, label, children }: FieldDetailModalProps) {
  return (
    <Modal centered show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">{label}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="my-account-field-modal-body">{children}</div>
      </Modal.Body>
    </Modal>
  );
}

type AccountBodyTextProps = {
  children: ReactNode;
  textForMeasure?: string;
  /** Label shown in the full-text modal */
  label?: string;
};

/** Multi-line clip for About / long free-text; "Show more" opens a modal */
export function AccountBodyText({
  children,
  textForMeasure,
  label,
}: AccountBodyTextProps) {
  const { t } = useTranslation();
  const textRef = useRef<HTMLParagraphElement>(null);
  const [showModal, setShowModal] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const modalTitle = label || t("account.fieldDetailTitle");

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const measure = () => {
      setOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children, textForMeasure]);

  return (
    <div>
      <p ref={textRef} className="my-account-about-text mb-0 is-clamped">
        {children}
      </p>
      {overflows ? (
        <button
          type="button"
          className="my-account-toggle-link"
          onClick={() => setShowModal(true)}
        >
          {t("account.showMore")}
        </button>
      ) : null}
      <FieldDetailModal
        show={showModal}
        onHide={() => setShowModal(false)}
        label={modalTitle}
      >
        {children}
      </FieldDetailModal>
    </div>
  );
}

function plainTextFromNode(value: ReactNode): string {
  if (value == null || typeof value === "boolean") return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return "";
}

type AccountTagFieldProps = {
  label: string;
  value: ReactNode;
  /** Plain string used for truncation / tooltip when value is not a plain string */
  textValue?: string;
};

export function AccountTagField({ label, value, textValue }: AccountTagFieldProps) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const fullText = (textValue ?? plainTextFromNode(value)).trim();
  const needsMore = fullText.length > TAG_MORE_CHAR_THRESHOLD;

  return (
    <div className="my-account-tag">
      <span className="my-account-tag__label" title={label}>
        {label}
      </span>
      <div className="d-flex align-items-baseline gap-1 min-w-0">
        <div
          className={`my-account-tag__value min-w-0 flex-grow-1${
            needsMore ? " is-truncated" : " is-expanded"
          }`}
          title={fullText || undefined}
        >
          {value}
        </div>
        {needsMore ? (
          <button
            type="button"
            className="my-account-tag__more flex-shrink-0"
            onClick={() => setShowModal(true)}
          >
            {t("account.more")}
          </button>
        ) : null}
      </div>
      <FieldDetailModal
        show={showModal}
        onHide={() => setShowModal(false)}
        label={label}
      >
        {value}
      </FieldDetailModal>
    </div>
  );
}

type AccountTagGridProps = {
  children: ReactNode;
};

export function AccountTagGrid({ children }: AccountTagGridProps) {
  return <div className="my-account-tags">{children}</div>;
}

type AccountContactRowProps = {
  label: string;
  value: ReactNode;
  title?: string;
};

export function AccountContactRow({ label, value, title }: AccountContactRowProps) {
  return (
    <div className="my-account-contact-row">
      <div className="my-account-contact-row__label">{label}</div>
      <div className="my-account-contact-row__value" title={title}>
        {value}
      </div>
    </div>
  );
}
